import { FC, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useCarerDetails from '@/api/useCarerDetails.ts';
import useRequestIntroCall from '@/api/useRequestIntroCall.ts';
import useCreateBookingRequest from '@/api/useCreateBookingRequest.ts';
import useCarerReviews from '@/api/useCarerReviews.ts';
import { Button } from 'react-daisyui';
import Routes from '@/router/Routes.ts';
import IntroCallRequestModal, { IntroCallRequestData } from '@/components/modals/IntroCallRequestModal.tsx';
import IntroCallConfirmationModal from '@/components/modals/IntroCallConfirmationModal.tsx';
import BookingRequestModal, { BookingRequestData } from '@/components/modals/BookingRequestModal.tsx';
import BookingConfirmationModal from '@/components/modals/BookingConfirmationModal.tsx';
import { trackEvent } from '@/utils/analytics.ts';
import { groupSkillsByCategory } from '@/constants/careSkills.ts';
import { formatCareType } from '@/utils/formatCareType.ts';

const CarerDetailsPage: FC = () => {
  const { carerId } = useParams<{ carerId: string }>();
  const navigate = useNavigate();
  const { data: carer, isLoading, isError } = useCarerDetails(carerId || '');
  const { data: reviews } = useCarerReviews(carerId || '');
  const { mutate: requestIntroCall, isPending: isRequestingIntroCall } = useRequestIntroCall();
  const { mutate: createBookingRequest, isPending: isCreatingBooking } = useCreateBookingRequest();

  useEffect(() => {
    if (carer) {
      trackEvent({
        name: 'profile_view',
        params: { verified_status: carer.verification_status === 'verified' },
      });
    }
  }, [carer]);
  const reviewsScrollRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isBookingConfirmationModalOpen, setIsBookingConfirmationModalOpen] = useState(false);

  const handleRequestIntroCall = (formData: IntroCallRequestData) => {
    if (carerId) {
      requestIntroCall(
        { 
          carer_id: carerId,
          ...formData
        },
        {
          onSuccess: () => {
            setIsRequestModalOpen(false);
            setIsConfirmationModalOpen(true);
          },
        }
      );
    }
  };

  const handleCreateBooking = (formData: BookingRequestData) => {
    if (carerId) {
      createBookingRequest(
        { 
          carer_id: carerId,
          ...formData
        },
        {
          onSuccess: () => {
            trackEvent({ name: 'booking_request_sent' });
            setIsBookingModalOpen(false);
            setIsBookingConfirmationModalOpen(true);
          },
        }
      );
    }
  };

  const handleOpenRequestModal = () => {
    setIsRequestModalOpen(true);
  };

  const handleOpenBookingModal = () => {
    setIsBookingModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };

  const handleCloseBookingConfirmationModal = () => {
    setIsBookingConfirmationModalOpen(false);
  };

  const handleSendMessage = () => {
    if (carerId) {
      navigate(Routes.MESSAGES_WITH_USER.replace(':userId', carerId));
    }
  };

  const handleScroll = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    rafRef.current = requestAnimationFrame(() => {
      if (reviewsScrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = reviewsScrollRef.current;
        const viewportRatio = (clientWidth / scrollWidth) * 100;
        const scrollRatio = (scrollLeft / scrollWidth) * 100;
        const progress = viewportRatio + scrollRatio;
        setScrollProgress(Math.min(progress, 100));
      }
    });
  };

  useEffect(() => {
    // Set initial progress on mount
    handleScroll();
    
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [carer]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 w-full animate-pulse">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(Routes.CARERS)}
            className="w-10 h-10 rounded-[12px] bg-black flex items-center justify-center text-white cursor-pointer"
          >
            <img src="/assets/arrowLeft.svg" alt="back" style={{ width: '16.5px', height: '15px' }} />
          </button>
          <div className="flex-1 h-8 bg-gray-200 rounded" />
        </div>
        <div className="flex flex-col gap-4 bg-white rounded-lg p-6">
          <div className="w-24 h-24 bg-gray-300 rounded-full mx-auto" />
          <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto" />
        </div>
      </div>
    );
  }

  if (isError || !carer) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(Routes.CARERS)}
            className="w-10 h-10 rounded-[12px] bg-black flex items-center justify-center text-white cursor-pointer"
          >
            <img src="/assets/arrowLeft.svg" alt="back" style={{ width: '16.5px', height: '15px' }} />
          </button>
        </div>
        <div className="text-center text-red-500 py-8">
          <p>Carer not found or error loading details.</p>
        </div>
      </div>
    );
  }



  return (
    <div className="flex flex-col gap-6 w-full ">
      {/* Header with back button and action buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <button
          onClick={() => navigate(Routes.CARERS)}
          className="w-[48px] h-[48px] rounded-[12px] bg-black flex items-center justify-center text-white hover:opacity-80 transition-opacity cursor-pointer flex-shrink-0"
        >
          <img src="/assets/arrowLeft.svg" alt="back" className="w-5 h-5" />
        </button>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            onClick={handleSendMessage}
            className="bg-black text-white border-none hover:opacity-90 rounded-[12px] px-6 w-full sm:w-auto flex items-center justify-center gap-2"
          >
            <span>Send message</span>
            <img src="/assets/message.svg" alt="" className="w-4 h-4" />
          </Button>
          <Button 
            className="bg-black text-white border-none hover:opacity-90 rounded-[12px] px-6 w-full sm:w-auto flex items-center justify-center gap-2"
            onClick={handleOpenRequestModal}
          >
            <span>Request intro call</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M0 2.25C0 1.00736 1.00736 0 2.25 0H3.27872C3.924 0 4.48648 0.439168 4.64298 1.06518L5.47233 4.38257C5.60956 4.93147 5.40445 5.50916 4.95182 5.84864L3.9816 6.5763C3.88085 6.65186 3.85895 6.76251 3.88725 6.83976C4.73888 9.16461 6.58539 11.0111 8.91024 11.8627C8.98749 11.8911 9.09814 11.8691 9.1737 11.7684L9.90136 10.7982C10.2408 10.3455 10.8185 10.1404 11.3674 10.2777L14.6848 11.107C15.3108 11.2635 15.75 11.826 15.75 12.4713V13.5C15.75 14.7426 14.7426 15.75 13.5 15.75H11.8125C5.28864 15.75 0 10.4614 0 3.9375V2.25Z" fill="white"/>
            </svg>
          </Button>
          <div className="relative group w-full sm:w-auto">
            <Button 
              className={`${
                !carer.has_completed_intro_call 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-black hover:opacity-90'
              } text-white border-none rounded-[12px] px-6 w-full flex items-center justify-center gap-2`}
              onClick={handleOpenBookingModal}
              disabled={!carer.has_completed_intro_call}
            >
              <span>Make Booking</span>
              <img src="/assets/makeBooking.svg" alt="" className="w-4 h-4" />
            </Button>
            {!carer.has_completed_intro_call && (
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                Complete an intro call first to make a booking
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[3px] border-r-[3px] border-b-[3px] border-transparent border-b-gray-900"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Section */}
      <div className="bg-[#F6F6F4] rounded-2xl p-4 sm:p-8">
        <div className="flex flex-col items-start gap-3 mb-6">
          {carer.photo_url ? (
            <img
              src={carer.photo_url}
              alt={carer.name}
              className="w-24 h-24 rounded-full object-contain flex-shrink-0"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-3xl font-semibold text-gray-500 flex-shrink-0">
              {carer.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex flex-row items-center gap-2 sm:gap-3">
            <h1 className="text-2xl font-semibold">{carer.name}</h1>
            {carer.verification_status === 'verified' && (
              <span className="bg-[#FED32C] text-black px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                <img src="/assets/verified.svg" alt="verified" className="w-5 h-5" />
                Verified
              </span>
            )}
          </div>
        </div>

        {/* About Me */}
        {carer.bio && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-[#9CA3AF] mb-2">About me</h2>
            <p className="text-base text-gray-800 leading-[]">{carer.bio}</p>
          </div>
        )}

        {/* Care Types */}
        {carer.care_types && carer.care_types.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-[#9CA3AF] mb-3">Care Types</h2>
            <div className="flex flex-wrap gap-2">
              {carer.care_types.map((type) => (
                <span key={type} className="text-base bg-white w-fit px-4 py-1 rounded-[120px] font-bold">
                  {formatCareType(type)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Skills — grouped by category */}
        {carer.skills && carer.skills.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-[#9CA3AF] mb-3">Skills</h2>
            <div className="flex flex-col gap-3">
              {groupSkillsByCategory(carer.skills).map(({ category, skills }) => (
                <div key={category.value}>
                  <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">
                    {category.label}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {skills.map((skill) => (
                      <span key={skill.value} className="text-base bg-white w-fit px-4 py-1 rounded-[120px] font-bold">
                        {skill.label}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hourly Rate */}
        {carer.hourly_rate && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-[#9CA3AF] mb-2">My Hourly Rate</h2>
            <p className="text-base bg-white w-fit px-4 py-1 rounded-[120px] font-bold">£{carer.hourly_rate}/hour</p>
          </div>
        )}

        {/* Service Area */}
        {carer.service_area && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-[#9CA3AF] text-gray-600 mb-2 gap-2">Areas I Cover</h2>
            <p className="text-base bg-white w-fit px-4 py-1 rounded-[120px] font-bold">{carer.service_area}</p>
          </div>
        )}
      </div>

      {/* Reviews Section */}
      {reviews && reviews.length > 0 && (
        <>
          {/* Progress Bar */}
          <div className="w-full h-[8px] bg-[#FEE68B] rounded-full">
            <div 
              className="h-full bg-[#FED32C] rounded-full transition-all duration-100 ease-out" 
              style={{ width: `${scrollProgress}%`, willChange: 'width' }} 
            />
          </div>

          <div 
            ref={reviewsScrollRef}
            onScroll={handleScroll}
            className="overflow-x-auto [&::-webkit-scrollbar]:hidden"
            style={{ 
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            <div className="flex gap-4">
              {reviews.map((review) => (
                <div 
                  key={review.id}
                  className="bg-[#F6F6F4] rounded-[12px] p-4 w-[38%] min-w-[280px] flex-shrink-0" 
                  style={{ boxShadow: '0px 0px 12px 0px rgba(0, 0, 0, 0.08)' }}
                >
                  <p className="text-sm font-semibold mb-2 text-[#9CA3AF]">{review.reviewer_name}</p>
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <img 
                        key={index}
                        src="/assets/star.svg" 
                        alt="star" 
                        className={`w-[33px] h-[33px] ${index >= review.rating ? 'opacity-30' : ''}`}
                      />
                    ))}
                  </div>
                  <p className="text-[16px] text-gray-700 font-normal" style={{ lineHeight: '200%', letterSpacing: '-0.5%', fontFamily: 'Roboto' }}>
                    {review.comment}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      <IntroCallRequestModal
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        onSubmit={handleRequestIntroCall}
        carerName={carer.name}
        isLoading={isRequestingIntroCall}
        availabilityDates={carer.availability_dates}
      />

      <IntroCallConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        carerName={carer.name}
      />

      <BookingRequestModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        onSubmit={handleCreateBooking}
        carerName={carer.name}
        isLoading={isCreatingBooking}
        availabilityDates={carer.availability_dates}
      />

      <BookingConfirmationModal
        isOpen={isBookingConfirmationModalOpen}
        onClose={handleCloseBookingConfirmationModal}
        carerName={carer.name}
      />
    </div>
  );
};

export default CarerDetailsPage;
